import React, { useMemo } from "react";
import AsyncSelect from "react-select/async";
import { CustomerService } from "../../services";
import { isCustomerSelectableForMedicalAppointment } from "../constants/customer.constant";

const CUSTOMER_SEARCH_PAGE_SIZE = 20;
const CUSTOMER_SEARCH_MAX_RESULTS = 50;
const CUSTOMER_SEARCH_DEBOUNCE_MS = 300;

export const MEDICAL_CUSTOMER_SELECT_STYLES = {
  control: (baseStyles) => ({
    ...baseStyles,
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    borderRadius: "8px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    paddingRight: "8px",
    color: "#333",
    "&:hover": { color: "#000" },
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    height: "43px",
    padding: "0 8px",
  }),
  // Do not set width/height on `input` — react-select sizes it via CSS grid; overrides break typing.
  input: (baseStyles) => ({
    ...baseStyles,
    margin: 0,
    padding: 0,
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    fontSize: "13px",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    margin: 0,
    fontSize: "13px",
  }),
};

export function customerToMedicalSelectOption(customer) {
  if (!customer?.id) return null;
  return {
    value: customer.id,
    label: customer.name || "",
  };
}

export async function loadMedicalAppointmentCustomerById(customerId) {
  if (!customerId) return null;
  try {
    const response = await CustomerService.getCustomer(customerId);
    const customer = response?.data;
    if (!customer || !isCustomerSelectableForMedicalAppointment(customer)) return null;
    return customer;
  } catch {
    return null;
  }
}

function parseMedicalAppointmentCustomerList(response) {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
}

async function fetchMedicalAppointmentCustomerOptions(inputValue) {
  const keyword = `${inputValue || ""}`.trim();
  const response = await CustomerService.listMedicalAppointmentCustomers({
    page: 1,
    pageSize: keyword ? CUSTOMER_SEARCH_MAX_RESULTS : CUSTOMER_SEARCH_PAGE_SIZE,
    keyword: keyword || undefined,
    fields: "summary",
  });
  return parseMedicalAppointmentCustomerList(response)
    .filter(isCustomerSelectableForMedicalAppointment)
    .map(customerToMedicalSelectOption)
    .filter(Boolean);
}

/**
 * Debounce async loadOptions while ensuring every returned promise settles.
 * (Clearing a timer without resolving leaves react-select stuck and blocks typing.)
 */
function createDebouncedLoadOptions(fetchFn, waitMs) {
  let timeoutId = null;
  let pendingResolve = null;

  return (inputValue) =>
    new Promise((resolve) => {
      if (pendingResolve) {
        pendingResolve([]);
      }
      pendingResolve = resolve;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(async () => {
        const activeResolve = pendingResolve;
        pendingResolve = null;
        timeoutId = null;
        try {
          const options = await fetchFn(inputValue);
          activeResolve?.(options);
        } catch (err) {
          console.error("Failed to load medical appointment customers", err);
          activeResolve?.([]);
        }
      }, waitMs);
    });
}

/**
 * Async customer picker for medical appointment forms. Loads a small page from the server
 * on open and searches as the user types.
 */
const MedicalCustomerAsyncSelect = ({
  value,
  onChange,
  styles = MEDICAL_CUSTOMER_SELECT_STYLES,
  placeholder = "Search customer by name or email…",
  isDisabled = false,
}) => {
  const loadOptions = useMemo(
    () => createDebouncedLoadOptions(fetchMedicalAppointmentCustomerOptions, CUSTOMER_SEARCH_DEBOUNCE_MS),
    []
  );

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      filterOption={null}
      value={value}
      onChange={onChange}
      styles={styles}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable
      noOptionsMessage={({ inputValue }) =>
        `${inputValue || ""}`.trim()
          ? `No customers matching "${inputValue.trim()}"`
          : "Type to search customers"
      }
      loadingMessage={() => "Loading customers…"}
    />
  );
};

export default MedicalCustomerAsyncSelect;
